import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { ObservableLevelsEnum, PartName } from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useMemo, useState, useRef } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import DropdownField from '../../../../components/atoms/DropdownField';
import { CheckboxWithComponents } from '../../../../components/atoms/CheckboxWithComponentsField';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import {
  SingleLineTextField,
  SingleLineValidation,
} from '../../../../components/atoms/SingleLineTextField';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { Label } from '../../../../components/atoms/Label';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { flattenObjectStrings } from '../../../../utils/flattenObjectString';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { ActionTypeStore } from '../../../../store/inspection/observation/actionTypeStore';
import { inspectionDB } from '../../../../data/database.structure';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';
import usePartDimensionForPath from '../../../../hooks/usePartDimensionForPath';
import WearDisplayHelpers from '../../../../helpers/wearDisplayHelpers';

export const ShoeRollerPathScreen = () => {
  const { externalId, sideName, partCategory } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    numberOfShoes,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory,
    partName: PartName.rollerPath,
  });
  const [error, setError] = useState<string | null>(null);

  const [validationError, setErrorValidation] =
    useState<SingleLineValidation>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);

  // #10205: acceptable range visible before the user types. DISPLAY-ONLY.
  const { partDimension, isLoaded: partDimensionLoaded } =
    usePartDimensionForPath({
      path: ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath.Wear
        .Measurement,
      equipmentPartExternalId: equipmentPart?.externalId,
    });

  useEffect(() => {
    setError(null); // reset error
    async function getEquipment() {
      try {
        if (errorEquipmentPart) {
          setError(errorEquipmentPart);
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [equipmentPart, errorEquipmentPart]);

  const listWearIndicators = useMemo(
    () => [
      {
        label: 'Toenailing',
        path: ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
          .RollerPath.WearIndicators.Toenailing.Checked,

        childPaths: [
          ObservableItemIdentifier.Identifiers.Undercarriage.Shoes.RollerPath
            .WearIndicators.Toenailing.Value,
        ],
      },
      {
        label: 'Spalling / Delamination / Minor Cracking',
        path: ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
          .RollerPath.WearIndicators.SpallingDelaminationMinorCracking.Checked,
        childPaths: [
          ObservableItemIdentifier.Identifiers.Undercarriage.Shoes.RollerPath
            .WearIndicators.SpallingDelaminationMinorCracking.Value,
        ],
      },
      {
        label: 'Severe Cracking / Broken',
        path: ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
          .RollerPath.WearIndicators.SevereCracking.Checked,
        childPaths: [
          ObservableItemIdentifier.Identifiers.Undercarriage.Shoes.RollerPath
            .WearIndicators.SevereCracking.Value,
        ],
      },
    ],
    [],
  );

  if (
    !equipmentPart ||
    !numberOfShoes ||
    !externalId ||
    !undercarriageCategory
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    // Phase 2 pilot (#10345): fields on this screen persist through the observation
    // draft layer - instant in-memory updates, one batched Dexie write per burst.
    <ObservationDraftScope inspectionId={externalId}>
      <div className="w-full">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            tag={sideName?.split('-')[0]}
            eyebrow={'Shoes'}
            title={'Roller path'}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-shoes-roller-path-screen-header'}
          />

          <div className="-mx-4 mt-2 bg-white">
            <ActionStatusBar
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entities={[
                {
                  entityId: equipmentPart.id,
                  entityExternalId: equipmentPart.externalId,
                },
              ]}
              pathsLike={flattenObjectStrings(
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .RollerPath.WearIndicators,
              )}
              paths={[
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Wear.Measurement,
              ]}
            />
          </div>
        </div>

        <ScreenObservationLayout>
          <Label
            titleType="fieldTitle"
            tooltipKey={'undercarriage-shoes-roller-path-worn-length'}
          >
            Measured Length
          </Label>

          <SingleLineTextField
            title="Measurement (mm)"
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath.Wear
                .Measurement
            }
            dataLevel={ObservableLevelsEnum.equipmentPart}
            tooltipKey={
              ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath.Wear
                .Measurement
            }
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            onChange={async (value) => {
              if (value === '') {
                return;
              }
              const valueFloat = parseFloat(value);
              if (isNaN(valueFloat)) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const observableItem = await inspectionDB.observableItems
                .where('identifier')
                .equals(
                  ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                    .Wear.Measurement,
                )
                .first();
              if (!observableItem) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const actionStatus =
                await new ActionTypeStore().getPartActionStatus({
                  equipmentPartId: equipmentPart.externalId,
                  value: valueFloat,
                  observableItemId: observableItem.id,
                });
              const isValid = ActionTypeStore.validateActionStatus({
                actionStatus: actionStatus,
                value: valueFloat,
              });
              if (
                !isValid &&
                (actionStatus.percentage < 0 || actionStatus.percentage > 250)
              ) {
                // #10205: rejection message includes the acceptable range
                setRangeError(
                  WearDisplayHelpers.formatOutOfRangeError({
                    value: valueFloat,
                    minValue: actionStatus.minValue,
                    maxValue: actionStatus.maxValue,
                  }),
                );
                setErrorValidation('BelowOrEqualZero');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              setRangeError(null);
            }}
            inspectionId={externalId}
            validationError={validationError}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            type="float"
            placeholder="Measurement (mm)"
          />
          {/* #10205: acceptable range visible before the user types */}
          {partDimensionLoaded && (
            <div className="text-sm text-inkMuted">
              {WearDisplayHelpers.formatAcceptableRangeHelperText({
                dimension: partDimension,
              })}
            </div>
          )}
          {rangeError && <p className="text-sm text-red-500">{rangeError}</p>}

          <Label
            titleType="fieldTitle"
            tooltipKey={'undercarriage-shoes-roller-path-wear-indicators'}
          >
            Wear Indicators
          </Label>
          {listWearIndicators.map((obj) => (
            <CheckboxWithComponents
              key={obj.path}
              {...obj}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              infoTooltipKey={`undercarriage-shoes-roller-path-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(obj.label)}`}
            >
              <DropdownField
                options={Array.from({ length: numberOfShoes }, (_, i) =>
                  (i + 1).toString(),
                )}
                placeholder="Number of shoes"
                path={obj.childPaths[0]}
                dataLevel={ObservableLevelsEnum.equipmentPart}
                entityId={equipmentPart.id}
                entityExternalId={equipmentPart.externalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  undercarriageCategory?.externalId ?? ''
                }
              />
            </CheckboxWithComponents>
          ))}
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                .Photo
            }
          />
          <RichTextField
            className="mt-4"
            label="Comments"
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                .Comments
            }
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
